import { useRef } from 'react';
import type { CaseView } from '@chemoflow/contracts';
import { selectors } from '@chemoflow/domain';
import { Icon, IconSwap } from '../components/Icon.js';
import {
  DoneArticle,
  DoneButton,
  ListSection,
  PatientWho,
  StatusPill,
  TRAIL_STEP,
  Trail,
  cls,
  liveStatus,
} from '../components/Card.js';
import { useCardMotion } from '../lib/motion.js';
import { useBoardData } from '../state/board.js';
import { useClock } from '../state/clock.js';
import { useUi } from '../state/ui.js';

/**
 * 薬剤待ちカード。
 * ベッドサイドでの片手操作を想定し、投与開始は1タップで入力できるようにする（要件 #7）。
 */
function NurseWaitCard({
  case: c,
  now,
  onStarted,
}: {
  case: CaseView;
  now: number;
  onStarted(c: CaseView): void;
}) {
  const { act, isBusy } = useBoardData();
  const { confirmTap } = useUi();
  // 送信中は止める。2回目のタップは版が古く、自分の操作が「他の端末で」として返る
  const busy = isBusy(c.caseId);

  const start = async () => {
    const ok = await confirmTap({
      action: '投与開始',
      name: c.patient?.name ?? '',
      patientId: c.patient?.patientId ?? c.surrogateKey,
      dept: c.patient?.dept ?? '',
      color: 'var(--admin)',
    });
    if (!ok) return;
    const updated = await act({ case: c, action: 'admin_start', silent: true });
    if (updated) onStarted(updated);
  };

  return (
    <article className="card nurse" data-id={c.caseId}>
      <div className="row">
        <StatusPill case={c} now={now} />
        <PatientWho case={c} />
        <Trail steps={[TRAIL_STEP.clerk(c), TRAIL_STEP.pharm(c), TRAIL_STEP.pharmDone(c)]} lg />
        {!c.clerk ? (
          <button className="btn start is-off" type="button" disabled>
            外来
            <br />
            入力待ち
          </button>
        ) : c.pharm ? (
          <button
            className={cls('btn start admin act', busy && 'is-off')}
            type="button"
            onClick={() => void start()}
            disabled={busy}
          >
            <IconSwap name="water_drop" />
            投与開始
          </button>
        ) : (
          <button className="btn start is-off" type="button" disabled>
            調製
            <br />
            待ち
          </button>
        )}
      </div>
    </article>
  );
}

/** 投与中カード。投与が終わったら「投与完了」を押して、下の投与完了の欄へ送る。 */
function NurseAdminCard({ case: c, now, onUndo }: { case: CaseView; now: number; onUndo(): void }) {
  const { act, isBusy } = useBoardData();
  const { confirmTap } = useUi();
  if (!c.admin) return null;
  const busy = isBusy(c.caseId);

  const complete = async () => {
    const ok = await confirmTap({
      action: '投与完了',
      name: c.patient?.name ?? '',
      patientId: c.patient?.patientId ?? c.surrogateKey,
      dept: c.patient?.dept ?? '',
      color: 'var(--admin)',
    });
    if (!ok) return;
    void act({ case: c, action: 'admin_done' });
  };

  return (
    <DoneArticle
      case={c}
      tone="admin"
      pill={liveStatus(c, now).node}
      label="投与開始"
      ts={c.admin.at}
      onUndo={onUndo}
      // 薬剤部の調製の状況も見せる。調製完了が入るまでは投与完了を押せない（点滴が届く途中で終了しないように）
      middle={
        c.pharmDone ? (
          <span className="since is-made">調製完了</span>
        ) : (
          <span className="since is-start">調製中</span>
        )
      }
      action={
        <DoneButton
          label="投与完了"
          icon="task_alt"
          action="admin_done"
          admin
          onClick={() => void complete()}
          disabled={busy || !c.pharmDone}
          disabledReason={c.pharmDone ? undefined : '薬剤部の調製完了を待っています'}
        />
      }
    />
  );
}

/** 化学療法室（看護師）：私の患者の薬は、どこまで来たか。 */
export function NurseView() {
  const { cases, undo } = useBoardData();
  const { now } = useClock();
  const { toast } = useUi();
  const viewRef = useRef<HTMLElement>(null);
  useCardMotion(viewRef, cases);

  const wait = selectors.nurseWait(cases);
  const administering = selectors.nurseAdmin(cases);
  const completed = selectors.nurseDone(cases);
  const stopped = selectors.nurseStop(cases);

  /** 押し間違いに備え、記録直後はトーストからも戻せるようにする（要件6-4）。 */
  const onStarted = (c: CaseView) => {
    toast(
      <>
        {c.patient?.name ?? '患者'}（{c.patient?.patientId ?? c.surrogateKey}）の <b>投与開始</b>{' '}
        を記録しました
      </>,
      { undo: () => void undo(c) },
    );
  };

  return (
    <main className="board is-rx view" id="view-nurse" data-view="nurse" ref={viewRef}>
      <section>
        <ListSection title="薬剤待ち" count={wait.length} emptyText="薬剤待ちの患者はいません">
          {wait.map((c) => (
            <NurseWaitCard key={c.caseId} case={c} now={now} onStarted={onStarted} />
          ))}
        </ListSection>
      </section>
      <section>
        <ListSection
          title="投与中"
          count={administering.length}
          emptyText="まだ投与開始はありません"
          headClass="is-admin"
        >
          {administering.map((c) => (
            <NurseAdminCard key={c.caseId} case={c} now={now} onUndo={() => void undo(c)} />
          ))}
        </ListSection>
        <ListSection
          title="投与完了"
          count={completed.length}
          emptyText="まだ投与完了はありません"
          headClass="is-done"
          icon="task_alt"
        >
          {completed.map((c) =>
            c.adminDone ? (
              <DoneArticle
                key={c.caseId}
                case={c}
                tone="done"
                pill={liveStatus(c, now).node}
                label="投与完了"
                ts={c.adminDone.at}
                middle={
                  <span className="since is-wait">
                    投与時間{' '}
                    <b>{Math.round((c.adminDone.at - (c.admin?.at ?? c.adminDone.at)) / 60000)}</b>
                    分
                  </span>
                }
                onUndo={() => void undo(c)}
              />
            ) : null,
          )}
        </ListSection>
        <ListSection title="本日中止" count={stopped.length} headClass="sub" hideWhenEmpty>
          {stopped.map((c) =>
            c.clerk ? (
              <DoneArticle
                key={c.caseId}
                case={c}
                tone="stop"
                pill={
                  <>
                    <Icon name="close" />
                    中止
                  </>
                }
                label="中止連絡"
                ts={c.clerk.at}
                extra="is-stopped"
              />
            ) : null,
          )}
        </ListSection>
      </section>
    </main>
  );
}
